import { useState, useEffect, useMemo } from 'react';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useUser } from '@/contexts/UserContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { filterInvoicesByWarehouse, filterByWarehouse } from '@/lib/warehouseFilter';
import { QRCodeDisplay, generateInvoiceQRValue } from '@/components/qr';
import { CopyButton } from '@/components/CopyButton';
import { useInvoices } from '@/hooks/useInvoices';
import { useCustomers } from '@/hooks/useCustomers';
import { useShipments } from '@/hooks/useShipments';
import { useTrips } from '@/hooks/useTrips';
import { useWarehouses } from '@/hooks/useWarehouses';
import { PayWithWalletDialog } from '@/components/PayWithWalletDialog';
import {
  FileText,
  Plus,
  Search,
  User,
  Package,
  DollarSign,
  Calendar,
  Download,
  Eye,
  Trash2,
  CheckCircle,
  Clock,
  XCircle,
  Printer,
  CreditCard,
  Truck,
  Phone,
  Filter,
  LayoutGrid,
  List,
  Mail
} from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface InvoiceShipment {
  shipmentNumber: string;
  description: string;
  weight: number;
  volume: number;
  packages: number;
  freightMethod: 'Air' | 'Sea';
  rate: number;
  amount: number;
}

interface InvoiceLineItem {
  description: string;
  amount: number;
}

interface Payment {
  amount: number;
  method: string;
  date: string;
  note?: string;
}

interface Invoice {
  id: string;
  invoiceNumber: string;
  customerCode: string;
  customerName: string;
  customerPhone?: string;
  customerEmail?: string;
  customerAddress?: string;
  destinationWarehouse: string;
  shipments: InvoiceShipment[];
  lineItems: InvoiceLineItem[];
  totalWeight: number;
  totalVolume: number;
  totalPackages: number;
  subtotal: number;
  tax: number;
  discount: number;
  total: number;
  amountPaid: number;
  balance: number;
  pricingModel: 'per_piece' | 'per_kg' | 'per_shipment';
  status: 'Draft' | 'Sent' | 'Paid' | 'Overdue' | 'Partial';
  issueDate: string;
  dueDate: string;
  paidDate?: string;
  payments: Payment[];
  tripName?: string;
}

interface InvoicesProps {
  filter?: 'all' | 'draft' | 'sent' | 'paid' | 'overdue' | 'partial';
}

export default function Invoices(props: InvoicesProps) {
  const { user } = useUser();
  const { t } = useLanguage();
  const { filter = 'all' } = props;

  const { invoices: apiInvoices, isLoading: invoicesLoading, createInvoice, updateInvoice, deleteInvoice } = useInvoices();
  const { customers: apiCustomers } = useCustomers();
  const { shipments: apiShipments } = useShipments();
  const { trips: apiTrips } = useTrips();
  const { warehouses: apiWarehouses } = useWarehouses();

  const allInvoices = (apiInvoices || []).map((inv: any) => {
    // Get shipments from the invoice - these come from the JOIN in the API
    const invoiceShipments = inv.shipments || [];
    
    // Calculate totals from shipments automatically
    const totalWeight = invoiceShipments.reduce((sum: number, s: any) => sum + (parseFloat(s.weight) || 0), 0);
    const totalVolume = invoiceShipments.reduce((sum: number, s: any) => sum + (parseFloat(s.volume) || 0), 0);
    const totalPackages = invoiceShipments.reduce((sum: number, s: any) => sum + (parseInt(s.packages) || 0), 0);
    
    // Build shipment details with rate and amount
    const rate = parseFloat(inv.rate_per_kg) || 0;
    const shipmentsWithDetails = invoiceShipments.map((s: any) => {
      const freightMethod = s.freightMethod || s.freight_method || 'Air';
      const weight = parseFloat(s.weight) || 0;
      const volume = parseFloat(s.volume) || 0;
      
      // Calculate amount based on freight method:
      // Air freight = rate * weight (per kg)
      // Sea freight = rate * volume (per CBM)
      const amount = freightMethod === 'Air' ? (weight * rate) : (volume * rate);
      
      return {
        shipmentNumber: s.shipmentNumber || s.shipment_number,
        description: s.description || 'Shipment',
        weight: weight,
        volume: volume,
        packages: parseInt(s.packages) || 0,
        freightMethod: freightMethod,
        rate: rate,
        amount: amount
      };
    });

    return {
      ...inv,
      id: inv.id || inv.invoice_number,
      issueDate: inv.issue_date,
      dueDate: inv.due_date,
      paidDate: inv.paid_date,
      customerCode: inv.customer_code,
      customerName: inv.customer_name,
      customerPhone: inv.customer_phone,
      customerEmail: inv.customer_email,
      customerAddress: inv.customer_address,
      destinationWarehouse: inv.destination_warehouse,
      tripName: inv.trip_name,
      pricingModel: inv.pricing_model,
      ratePerKg: rate,
      ratePerPiece: parseFloat(inv.rate_per_piece) || 0,
      ratePerShipment: parseFloat(inv.rate_per_shipment) || 0,
      subtotal: parseFloat(inv.subtotal) || 0,
      discount: parseFloat(inv.discount) || 0,
      tax: parseFloat(inv.tax) || 0,
      total: parseFloat(inv.total) || 0,
      amountPaid: parseFloat(inv.amount_paid) || 0,
      balanceDue: parseFloat(inv.balance_due) || 0,
      balance: parseFloat(inv.balance_due) || 0,
      totalVolume: totalVolume,
      invoiceNumber: inv.invoice_number,
      totalWeight: totalWeight,
      totalPackages: totalPackages,
      shipments: shipmentsWithDetails,
      lineItems: inv.lineItems || [],
      payments: inv.payments || [],
      status: inv.status,
    };
  });

  const customers = apiCustomers || [];
  const shipments = apiShipments || [];
  const trips = apiTrips || [];
  const warehouses = apiWarehouses || [];

  // Old state - keeping for compatibility
  const [, setAllInvoices] = useState<Invoice[]>(() => {
    return [
      {
        id: '1',
        invoiceNumber: '1234-00001',
        customerCode: '1234',
        customerName: 'John Doe',
        customerPhone: '+1 (555) 123-4567',
        destinationWarehouse: 'WH-001',
        shipments: [],
        lineItems: [],
        totalWeight: 0,
        totalPackages: 0,
        subtotal: 0,
        tax: 0,
        discount: 0,
        total: 0,
        amountPaid: 0,
        balance: 0,
        pricingModel: 'per_kg',
        status: 'Draft',
        issueDate: '2024-01-15',
        dueDate: '2024-02-15',
        payments: []
      },
      {
      id: '2',
      invoiceNumber: '5678-00001',
      customerCode: '5678',
      customerName: 'Jane Smith',
      customerPhone: '+1 (555) 987-6543',
      destinationWarehouse: 'WH-003',
      shipments: [
        { shipmentNumber: 'SHP-002', description: 'Furniture items', weight: 220.0, packages: 8, rate: 12, amount: 2640 }
      ],
      lineItems: [],
      totalWeight: 220.0,
      totalPackages: 8,
      subtotal: 2640,
      tax: 0,
      discount: 0,
      total: 2640,
      amountPaid: 2640,
      balance: 0,
      pricingModel: 'per_kg',
      status: 'Paid',
      issueDate: '2024-01-10',
      dueDate: '2024-02-10',
      paidDate: '2024-01-25',
      payments: []
    },
    {
      id: '3',
      invoiceNumber: '9012-00001',
      customerCode: '9012',
      customerName: 'Bob Johnson',
      customerPhone: '+1 (555) 456-7890',
      destinationWarehouse: 'WH-001',
      shipments: [
        { shipmentNumber: 'SHP-003', description: 'Industrial supplies', weight: 180.5, packages: 6, rate: 11, amount: 1985.5 }
      ],
      lineItems: [],
      totalWeight: 180.5,
      totalPackages: 6,
      subtotal: 1985.5,
      tax: 0,
      discount: 0,
      total: 1985.5,
      amountPaid: 0,
      balance: 1985.5,
      pricingModel: 'per_kg',
      status: 'Overdue',
      issueDate: '2023-12-15',
      dueDate: '2024-01-15',
      payments: []
    },
    {
      id: '4',
      invoiceNumber: '3456-00001',
      customerCode: '3456',
      customerName: 'Alice Brown',
      customerPhone: '+1 (555) 234-5678',
      destinationWarehouse: 'WH-003',
      shipments: [
        { shipmentNumber: 'SHP-004', description: 'Medical supplies', weight: 95.0, packages: 4, rate: 15, amount: 1425 }
      ],
      lineItems: [],
      totalWeight: 95.0,
      totalPackages: 4,
      subtotal: 1425,
      tax: 0,
      discount: 0,
      total: 1425,
      amountPaid: 0,
      balance: 1425,
      pricingModel: 'per_kg',
      status: 'Sent',
      issueDate: '2024-01-16',
      dueDate: '2024-02-16',
      payments: []
    },
    {
      id: '5',
      invoiceNumber: '7890-00001',
      customerCode: '7890',
      customerName: 'Charlie Wilson',
      customerPhone: '+1 (555) 345-6789',
      destinationWarehouse: 'WH-002',
      shipments: [
        { shipmentNumber: 'SHP-005', description: 'Construction materials', weight: 300.0, packages: 12, rate: 8, amount: 2400 }
      ],
      lineItems: [],
      totalWeight: 300.0,
      totalPackages: 12,
      subtotal: 2400,
      tax: 0,
      discount: 0,
      total: 2400,
      amountPaid: 1200,
      balance: 1200,
      pricingModel: 'per_kg',
      status: 'Partial',
      issueDate: '2024-01-14',
      dueDate: '2024-02-14',
      payments: [
        { date: '2024-01-20', amount: 1200, method: 'Bank Transfer', reference: 'PAY-001' }
      ]
    }
  ];
}, []);

  // Filter invoices based on user role and access
  const invoices = useMemo(() => {
    // If user is customer, show only their invoices
    if (user?.role === 'customer') {
      return allInvoices.filter(invoice => invoice.customerCode === user.customerCode);
    }
    // For admin and warehouse users, use the same filter that works for trips
    return filterByWarehouse(allInvoices, user);
  }, [allInvoices, user]);

  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [warehouseFilter, setWarehouseFilter] = useState<string>('all');
  const [tripFilter, setTripFilter] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [showPayDialog, setShowPayDialog] = useState(false);
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState<Invoice | null>(null);
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingInvoice, setEditingInvoice] = useState<Invoice | null>(null);
  const [selectedShipments, setSelectedShipments] = useState<string[]>([]);
  const [newInvoice, setNewInvoice] = useState({
    customerCode: '',
    customerName: '',
    customerPhone: '',
    destinationWarehouse: '',
    tripName: '',
    rate: '10',
    tax: '0'
  });

  // Get available trips for warehouse users
  const getAvailableTrips = () => {
    return trips.filter(trip =>
      ['Planning', 'In Transit'].includes(trip.status) &&
      (user?.role === 'admin' ||
       trip.destination_warehouse === user?.warehouseCode ||
       trip.destinationWarehouse === user?.warehouseCode ||
       trip.origin_warehouse === user?.warehouseCode ||
       trip.originWarehouse === user?.warehouseCode)
    );
  };

  // Update customer name and phone when customer code changes
  useEffect(() => {
    if (newInvoice.customerCode) {
      const customerList = customers || []; // Use the state variable
      const customer = customerList.find(c => c.code === newInvoice.customerCode);
      setNewInvoice(prev => ({
        ...prev,
        customerName: customer ? customer.name : '',
        customerPhone: customer ? customer.phone : ''
      }));
    }
  }, [newInvoice.customerCode, customers, setNewInvoice]);



  // Function to check if a shipment already has an invoice
  const isShipmentAlreadyInvoiced = (shipmentNumber: string) => {
    return allInvoices.some(invoice =>
      invoice.shipments.some(shipment =>
        shipment.shipmentNumber === shipmentNumber
      )
    );
  };

  // Function to get invoice info for a shipment
  const getShipmentInvoiceInfo = (shipmentNumber: string) => {
    const invoice = allInvoices.find(inv =>
      inv.shipments.some(shipment =>
        shipment.shipmentNumber === shipmentNumber
      )
    );
    return invoice ? {
      invoiceNumber: invoice.invoiceNumber,
      status: invoice.status
    } : null;
  };

  // Function to clean up orphaned shipments from invoices
  const cleanupOrphanedShipments = () => {
    const savedShipments = localStorage.getItem('shiphub-shipments');
    const currentShipments = savedShipments ? JSON.parse(savedShipments) : [];
    const currentShipmentNumbers = new Set(currentShipments.map((s: any) => s.shipmentNumber));
    console.log('🔍 Invoices Cleanup - Current shipments:', currentShipments.length);
    console.log('🔍 Invoices Cleanup - Current shipment numbers:', Array.from(currentShipmentNumbers));
    console.log('🔍 Invoices Cleanup - Invoices before cleanup:', allInvoices.length, allInvoices);

    // Clean up invoices
    const updatedInvoices = invoices.map((invoice: any) => {
      if (invoice.shipments && Array.isArray(invoice.shipments)) {
        console.log('🔍 Invoices Cleanup - Processing invoice:', invoice.invoiceNumber, 'Shipments:', invoice.shipments.length);

        const validShipments = invoice.shipments.filter((invoiceShipment: any) => {
          const isValid = currentShipmentNumbers.has(invoiceShipment.shipmentNumber);
          if (!isValid) {
            console.log('🔍 Invoices Cleanup - Removing orphaned shipment:', invoiceShipment.shipmentNumber, 'from invoice:', invoice.invoiceNumber);
          }
          return isValid;
        });

        console.log('🔍 Invoices Cleanup - Valid shipments after filter:', validShipments.length);

        // Recalculate total amount
        const totalAmount = validShipments.reduce((sum: number, shipment: any) =>
          sum + (shipment.amount || 0), 0
        );

        return {
          ...invoice,
          shipments: validShipments,
          totalAmount
        };
      }
      return invoice;
    });

    console.log('🔍 Invoices Cleanup - Invoices after cleanup:', updatedInvoices.length, updatedInvoices);
    setAllInvoices(updatedInvoices);
    localStorage.setItem('shiphub-invoices', JSON.stringify(updatedInvoices));
  };

  // Get all shipment numbers that are already in invoices
  const getInvoicedShipmentNumbers = () => {
    const invoicedShipmentNumbers = new Set<string>();
    allInvoices.forEach(invoice => {
      if (invoice.shipments && Array.isArray(invoice.shipments)) {
        invoice.shipments.forEach((shipment: any) => {
          const shipmentNum = typeof shipment === 'string' ? shipment : shipment.shipmentNumber;
          if (shipmentNum) {
            invoicedShipmentNumbers.add(shipmentNum);
          }
        });
      }
    });
    return invoicedShipmentNumbers;
  };

  // Update available shipments based on current data
  const getAvailableShipments = () => {
    const invoicedShipmentNumbers = getInvoicedShipmentNumbers();

    return shipments
      .filter((shipment: any) => {
        // Filter by customer code if provided
        if (newInvoice.customerCode) {
          if ((shipment.customerCode || '') !== newInvoice.customerCode) {
            return false;
          }
        }

        // Filter by trip if selected
        if (newInvoice.tripName) {
          return (shipment.tripName || '') === newInvoice.tripName;
        }

        // For warehouse users, only show shipments for their warehouse
        if (user?.role === 'warehouse' && user?.warehouseCode) {
          return shipment.destinationWarehouse === user.warehouseCode;
        }

        return true; // Admins see all shipments if no filters applied
      })
      .map((shipment: any) => ({
        number: shipment.shipmentNumber || '',
        customer: shipment.customerName || '',
        customerCode: shipment.customerCode || '',
        weight: shipment.weight || 0,
        volume: shipment.volume || 0,
        packages: shipment.packages || 0,
        method: shipment.freightMethod || 'Air',
        description: shipment.description || '',
        tripName: shipment.tripName || '',
        isInvoiced: invoicedShipmentNumbers.has(shipment.shipmentNumber) // Add flag
      }));
  };

  // Mock available shipments for creating invoices
  const availableShipments = getAvailableShipments();

  // Cleanup disabled to prevent invoice deletion issues
  // useEffect(() => {
  //   console.log('🔍 Invoices Page - Running cleanup on mount');
  //   cleanupOrphanedShipments();
  // }, []);

  // Save invoices to localStorage whenever they change
  useEffect(() => {
    localStorage.setItem('shiphub-invoices', JSON.stringify(allInvoices));
  }, [allInvoices]);

  const generateInvoiceNumber = () => {
    if (!newInvoice.customerCode) {
      return 'INV-00001'; // Fallback if no customer code
    }

    const customerCode = newInvoice.customerCode;

    // Find existing invoices for this customer
    const customerInvoices = invoices.filter(inv =>
      inv.invoiceNumber.startsWith(`${customerCode}-`)
    );

    // Extract numbers from customer's existing invoices
    const existingNumbers = customerInvoices.map(inv => {
      const numberPart = inv.invoiceNumber.replace(`${customerCode}-`, '');
      return parseInt(numberPart) || 0;
    });

    // Handle empty array case
    const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
    return `${customerCode}-${nextNumber.toString().padStart(5, '0')}`;
  };

  const filteredInvoices = invoices.filter(invoice => {
    const invoiceNumber = invoice.invoiceNumber || '';
    const customerName = invoice.customerName || '';
    const customerCode = invoice.customerCode || '';
    const status = invoice.status || '';
    const destinationWarehouse = invoice.destinationWarehouse || '';
    const tripName = invoice.tripName || '';
    const issueDate = invoice.issueDate || '';

    const searchMatch = (
      invoiceNumber.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customerCode.includes(searchTerm)
    );

    // Status filter matching based on URL parameter
    let statusMatch = true;
    if (filter === 'draft') {
      statusMatch = status === 'Draft';
    } else if (filter === 'sent') {
      statusMatch = status === 'Sent';
    } else if (filter === 'paid') {
      statusMatch = status === 'Paid';
    } else if (filter === 'overdue') {
      statusMatch = status === 'Overdue';
    } else if (filter === 'partial') {
      statusMatch = status === 'Partial';
    }

    // Warehouse filter
    const warehouseMatch = warehouseFilter === 'all' || destinationWarehouse === warehouseFilter;

    // Trip filter
    const tripMatch = tripFilter === 'all' || tripName === tripFilter;

    // Date range filter
    let dateMatch = true;
    if (dateRange.startDate && dateRange.endDate && issueDate) {
      const invoiceDate = new Date(issueDate);
      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);
      end.setHours(23, 59, 59, 999); // Include end date fully
      dateMatch = invoiceDate >= start && invoiceDate <= end;
    }

    return searchMatch && statusMatch && warehouseMatch && tripMatch && dateMatch;
  }).sort((a, b) => {
    const dateA = new Date(a.issueDate || '1970-01-01').getTime();
    const dateB = new Date(b.issueDate || '1970-01-01').getTime();
    return dateB - dateA;
  });

  // Get unique warehouses and trips for filter dropdowns
  const uniqueWarehouses = Array.from(new Set(invoices.map(inv => inv.destinationWarehouse))).filter(Boolean);
  const uniqueTrips = Array.from(new Set(invoices.map(inv => inv.tripName))).filter(Boolean);

  const handleToggleShipment = (shipmentNumber: string) => {
    // Prevent selecting already invoiced shipments
    if (isShipmentAlreadyInvoiced(shipmentNumber)) {
      return;
    }

    setSelectedShipments(prev =>
      prev.includes(shipmentNumber)
        ? prev.filter(s => s !== shipmentNumber)
        : [...prev, shipmentNumber]
    );
  };

  const handleCreateInvoice = () => {
    // Validation
    if (!newInvoice.destinationWarehouse) {
      alert('Please select a destination warehouse for this invoice.');
      return;
    }

    // Validate customer is in the allowed list for warehouse users
    if (user?.role === 'warehouse') {
      const isValidCustomer = customers.some(c => c.code === newInvoice.customerCode);
      if (!isValidCustomer) {
        alert('Invalid customer code. You can only create invoices for customers in your warehouse area.');
        return;
      }
    }

    const rate = parseFloat(newInvoice.rate);
    const tax = parseFloat(newInvoice.tax) || 0;
    const selectedShipmentData = availableShipments.filter(s =>
      selectedShipments.includes(s.number)
    );

    const totalWeight = selectedShipmentData.reduce((sum, s) => sum + s.weight, 0);
    const totalVolume = selectedShipmentData.reduce((sum, s) => sum + s.volume, 0);
    const totalPackages = selectedShipmentData.reduce((sum, s) => sum + s.packages, 0);
    
    // Determine freight method from first shipment (all shipments in invoice should have same method)
    const freightMethod = selectedShipmentData[0]?.method || 'Air';
    
    // Calculate subtotal based on freight method:
    // Air freight = rate * total weight (per kg)
    // Sea freight = rate * total volume (per CBM)
    const subtotal = freightMethod === 'Air' 
      ? totalWeight * rate 
      : totalVolume * rate;
    
    const total = subtotal - tax; // Tax field is actually discount, so subtract

    createInvoice({
      customer_code: newInvoice.customerCode,
      customer_name: newInvoice.customerName,
      customer_phone: newInvoice.customerPhone || null,
      destination_warehouse: newInvoice.destinationWarehouse,
      trip_name: newInvoice.tripName || null,
      pricing_model: 'per_kg',
      rate_per_kg: rate,
      subtotal: subtotal,
      discount: 0,
      tax: tax,
      total: total,
      amount_paid: 0,
      balance_due: total,
      status: 'Draft',
      issue_date: new Date().toISOString().split('T')[0],
      due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      shipments: selectedShipments
    });

    setNewInvoice({ customerCode: '', customerName: '', customerPhone: '', destinationWarehouse: '', tripName: '', rate: '10', tax: '0' });
    setSelectedShipments([]);
    setIsAddDialogOpen(false);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Paid': return 'bg-green-100 text-green-800';
      case 'Sent': return 'bg-blue-100 text-blue-800';
      case 'Draft': return 'bg-gray-100 text-gray-800';
      case 'Overdue': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Paid': return <CheckCircle className="h-4 w-4" />;
      case 'Sent': return <Clock className="h-4 w-4" />;
      case 'Draft': return <FileText className="h-4 w-4" />;
      case 'Overdue': return <XCircle className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  const updateInvoiceStatus = (invoiceNumber: string, newStatus: Invoice['status']) => {
    // Find the invoice to get its total
    const invoice = allInvoices.find(inv => inv.invoiceNumber === invoiceNumber);
    
    if (!invoice) return;

    const updateData: any = {
      invoice_number: invoiceNumber,
      status: newStatus,
    };

    // When marking as paid, update payment fields
    if (newStatus === 'Paid') {
      updateData.paid_date = new Date().toISOString().split('T')[0];
      updateData.amount_paid = parseFloat(invoice.total.toString());
      updateData.balance_due = 0;
    } 
    // When changing FROM Paid to any other status, clear payment data
    else if (invoice.status === 'Paid' && newStatus !== 'Paid') {
      updateData.paid_date = null;
      updateData.amount_paid = 0;
      updateData.balance_due = parseFloat(invoice.total.toString());
    }

    updateInvoice(updateData);
  };

  const handleDeleteInvoice = (invoiceNumber: string) => {
    if (confirm('Are you sure you want to delete this invoice? This action cannot be undone.')) {
      deleteInvoice(invoiceNumber);
    }
  };

  const handleEditInvoice = (invoice: Invoice) => {
    setEditingInvoice(invoice);
    setIsEditDialogOpen(true);
  };

  const handleUpdateInvoice = () => {
    if (!editingInvoice) return;

    // Get the rate from the first shipment (all shipments have same rate)
    const rate = editingInvoice.shipments[0]?.rate || 0;

    const updateData: any = {
      invoice_number: editingInvoice.invoiceNumber,
      customer_code: editingInvoice.customerCode,
      customer_name: editingInvoice.customerName,
      customer_phone: editingInvoice.customerPhone,
      trip_name: editingInvoice.tripName || null,
      issue_date: editingInvoice.issueDate,
      due_date: editingInvoice.dueDate,
      rate_per_kg: rate,
      subtotal: editingInvoice.subtotal,
      tax: editingInvoice.tax,
      total: editingInvoice.total,
      amount_paid: editingInvoice.amountPaid || 0,
      balance_due: editingInvoice.balance,
      status: editingInvoice.status,
      notes: editingInvoice.notes
    };

    // When changing FROM Paid to any other status, clear payment data
    const originalInvoice = allInvoices.find(inv => inv.invoiceNumber === editingInvoice.invoiceNumber);
    if (originalInvoice && originalInvoice.status === 'Paid' && editingInvoice.status !== 'Paid') {
      updateData.paid_date = null;
      updateData.amount_paid = 0;
      updateData.balance_due = parseFloat(editingInvoice.total.toString());
    }
    // When changing TO Paid, set payment data
    else if (editingInvoice.status === 'Paid' && (!originalInvoice || originalInvoice.status !== 'Paid')) {
      updateData.paid_date = new Date().toISOString().split('T')[0];
      updateData.amount_paid = parseFloat(editingInvoice.total.toString());
      updateData.balance_due = 0;
    }

    updateInvoice(updateData);

    setIsEditDialogOpen(false);
    setEditingInvoice(null);
  };

  // Print Invoice
  const handlePrintInvoice = (invoice: Invoice) => {
    // Get full shipment data with items from shipments array
    const invoiceShipmentNumbers = invoice.shipments.map(s => s.shipmentNumber);
    const fullShipments = shipments.filter(s => invoiceShipmentNumbers.includes(s.shipmentNumber));
    
    // Generate QR code as base64 image
    const qrValue = generateInvoiceQRValue(invoice.invoiceNumber);
    const canvas = document.createElement('canvas');
    import('qrcode').then(QRCode => {
      QRCode.default.toCanvas(canvas, qrValue, { width: 150, margin: 1 }, (error) => {
        if (error) console.error(error);
        const qrImageUrl = canvas.toDataURL();

        const printWindow = window.open('', '_blank');
        if (!printWindow) return;

        const logoUrl = window.location.origin + '/logo-full.jpg';
        
        // Determine if this is Air or Sea freight (check first shipment)
        const freightMethod = fullShipments[0]?.freightMethod || invoice.shipments[0]?.freightMethod || 'Air';
        const isAirFreight = freightMethod === 'Air';
        
        // Dynamic pricing label based on freight method
        const pricingLabel = isAirFreight ? 'Price (per kg)' : 'Price (per CBM)';
        
        // Get Main warehouse info for company details
        const mainWarehouse = apiWarehouses?.find((w: any) => w.type === 'Main') || apiWarehouses?.[0];
        const companyPhone = mainWarehouse?.phone || '+86 130 4806 4436';
        const companyEmail = 'info@sourcemygoods.com';
        const companyAddress = 'Office 407, No.6, Yuanjing Road, Tianhe District, Guangzhou, China';

        printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Invoice - ${invoice.invoiceNumber}</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            @page {
              size: A4 portrait;
              margin: 10mm;
            }
            body {
              font-family: Arial, Helvetica, sans-serif;
              font-size: 9pt;
              line-height: 1.4;
              color: #1a1a2e;
              background: #fff;
              padding: 12px;
            }

            /* ── PAGE HEADER ── */
            .doc-header {
              display: flex;
              align-items: center;
              gap: 12px;
              padding-bottom: 8px;
              margin-bottom: 12px;
              border-bottom: 2px solid #1e3a6e;
            }
            .doc-header img.logo {
              height: 50px;
              width: auto;
              object-fit: contain;
              flex-shrink: 0;
            }
            .doc-header .vertical-line {
              width: 2px;
              height: 50px;
              background: #1e3a6e;
              flex-shrink: 0;
            }
            .doc-header .company-info {
              flex: 1;
              font-size: 8pt;
              color: #555;
              line-height: 1.5;
            }
            .doc-header .company-info strong { 
              color: #1e3a6e; 
              font-size: 8.5pt;
            }

            /* ── INVOICE TITLE BAND ── */
            .title-band {
              background: linear-gradient(135deg, #1e3a6e 0%, #2563eb 100%);
              color: #fff;
              padding: 8px 12px;
              border-radius: 4px;
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 12px;
            }
            .title-band h1 { font-size: 14pt; letter-spacing: 1px; margin: 0; }
            .title-band .invoice-meta { text-align: right; font-size: 8pt; line-height: 1.5; }
            .title-band .invoice-meta strong { font-size: 10pt; display: block; }

            /* ── STATUS BADGE ── */
            .status-badge {
              display: inline-block;
              padding: 2px 10px;
              border-radius: 20px;
              font-size: 7.5pt;
              font-weight: bold;
              text-transform: uppercase;
            }
            .status-paid     { background: #d4edda; color: #155724; }
            .status-sent     { background: #d1ecf1; color: #0c5460; }
            .status-draft    { background: #e9ecef; color: #495057; }
            .status-overdue  { background: #f8d7da; color: #721c24; }
            .status-partial  { background: #fff3cd; color: #856404; }

            /* ── INFO BOXES ── */
            .info-row {
              display: flex;
              gap: 10px;
              margin-bottom: 12px;
            }
            .info-box {
              flex: 1;
              border: 1px solid #dee2e6;
              border-radius: 4px;
              padding: 8px 10px;
              background: #f8f9fa;
              page-break-inside: avoid;
            }
            .info-box h3 {
              font-size: 8pt;
              text-transform: uppercase;
              color: #1e3a6e;
              border-bottom: 1px solid #1e3a6e;
              padding-bottom: 3px;
              margin-bottom: 6px;
              letter-spacing: 0.5px;
            }
            .info-box p { font-size: 8.5pt; margin-bottom: 2px; }

            /* ── QR CODE PANEL ── */
            .qr-panel {
              text-align: center;
              border: 1px solid #dee2e6;
              border-radius: 4px;
              padding: 8px;
              background: #f8f9fa;
              width: 110px;
              flex-shrink: 0;
            }
            .qr-panel img { width: 90px; height: 90px; }
            .qr-panel p { font-size: 6.5pt; color: #888; margin-top: 4px; }

            /* ── SECTION TITLE ── */
            .section-title {
              font-size: 9pt;
              font-weight: bold;
              color: #fff;
              background: linear-gradient(135deg, #1e3a6e 0%, #2563eb 100%);
              padding: 4px 8px;
              border-radius: 3px;
              margin: 12px 0 6px 0;
            }

            /* ── TABLES ── */
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 7.5pt;
              page-break-inside: auto;
            }
            thead tr {
              background: #1e3a6e;
              color: #fff;
            }
            th {
              padding: 5px 6px;
              text-align: left;
              font-weight: 600;
              font-size: 8pt;
            }
            td {
              padding: 4px 6px;
              border-bottom: 1px solid #e9ecef;
            }
            tbody tr:nth-child(even) { background: #f8f9fa; }
            td.right, th.right { text-align: right; }
            tr { page-break-inside: avoid; }

            /* ── SHIPMENT TOTALS BOX ── */
            .totals-box {
              margin-top: 12px;
              padding: 10px;
              border: 2px solid #1e3a6e;
              border-radius: 4px;
              background: #f8f9fa;
              max-width: 300px;
              margin-left: auto;
              page-break-inside: avoid;
            }
            .totals-box table { margin: 0; font-size: 8pt; }
            .totals-box tr { border-bottom: 1px solid #dee2e6; }
            .totals-box tr:last-child { border-bottom: none; }
            .totals-box th {
              background: none;
              color: #555;
              padding: 5px 6px;
              font-weight: 600;
              text-align: left;
            }
            .totals-box td {
              padding: 5px 6px;
              border: none;
              text-align: right;
              font-weight: bold;
              color: #1e3a6e;
            }

            /* ── TOTALS TABLE ── */
            .totals-wrap {
              display: flex;
              justify-content: flex-end;
              margin-top: 10px;
              page-break-inside: avoid;
            }
            .totals-table {
              width: 240px;
              border: 1px solid #dee2e6;
              border-radius: 4px;
              overflow: hidden;
            }
            .totals-table td {
              padding: 5px 8px;
              border-bottom: 1px solid #e9ecef;
              font-size: 8.5pt;
            }
            .totals-table .label-col { color: #555; }
            .totals-table .val-col   { text-align: right; font-weight: 600; }
            .totals-table .grand-total td {
              background: linear-gradient(135deg, #1e3a6e 0%, #2563eb 100%);
              color: #fff;
              font-size: 10pt;
              font-weight: bold;
              border-bottom: none;
            }
            .totals-table .orange-row td {
              background: #fff8ed;
              color: #92400e;
              font-weight: 600;
            }

            /* ── FOOTER ── */
            .doc-footer {
              margin-top: 20px;
              padding-top: 8px;
              border-top: 1px solid #dee2e6;
              text-align: center;
              font-size: 7.5pt;
              color: #888;
              line-height: 1.6;
              page-break-inside: avoid;
            }
            .doc-footer strong { color: #1e3a6e; }

            @media print {
              body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
            }
          </style>
        </head>
        <body>

          <!-- HEADER -->
          <div class="doc-header">
            <img class="logo" src="${logoUrl}" alt="SourceMyGoods" onerror="this.style.display='none'"/>
            <div class="vertical-line"></div>
            <div class="company-info">
              Phone: ${companyPhone}<br/>
              Email: ${companyEmail}<br/>
              Address: ${companyAddress}
            </div>
          </div>

          <!-- TITLE BAND -->
          <div class="title-band">
            <h1>INVOICE</h1>
            <div class="invoice-meta">
              <strong>${invoice.invoiceNumber}</strong>
              <span class="status-badge status-${invoice.status.toLowerCase()}">${invoice.status}</span>
            </div>
          </div>

          <!-- INFO ROW -->
          <div class="info-row">
            <div class="info-box">
              <h3>Bill To</h3>
              <p><strong>${invoice.customerName}</strong></p>
              <p>Code: ${invoice.customerCode}</p>
              ${invoice.customerPhone ? `<p>Phone: ${invoice.customerPhone}</p>` : ''}
              ${invoice.customerEmail ? `<p>Email: ${invoice.customerEmail}</p>` : ''}
              ${invoice.customerAddress ? `<p>Address: ${invoice.customerAddress}</p>` : ''}
            </div>
            <div class="info-box">
              <h3>Invoice Details</h3>
              <p><strong>Issue Date:</strong> ${invoice.issueDate}</p>
              <p><strong>Due Date:</strong> ${invoice.dueDate}</p>
              ${invoice.paidDate ? `<p><strong>Paid Date:</strong> ${invoice.paidDate}</p>` : ''}
              ${invoice.tripName ? `<p><strong>Trip:</strong> ${invoice.tripName}</p>` : ''}
              <p><strong>Freight Method:</strong> ${isAirFreight ? 'Air Freight' : 'Sea Freight'}</p>
              <p><strong>Pricing:</strong> ${isAirFreight ? 'Per KG' : 'Per CBM'}</p>
            </div>
            <div class="qr-panel">
              <img src="${qrImageUrl}" alt="QR Code" />
              <p>SCAN TO UPDATE</p>
            </div>
          </div>

          <!-- SHIPMENT TABLE WITH ITEMS -->
          <div class="section-title">Shipment Details</div>
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Shipment No.</th>
                <th>Item Description</th>
                <th class="right">Qty</th>
                <th class="right">Boxes</th>
                <th class="right">Weight (kg)</th>
                <th class="right">Volume (CBM)</th>
                <th class="right">${pricingLabel}</th>
                <th class="right">Amount ($)</th>
              </tr>
            </thead>
            <tbody>
              ${fullShipments.map((shipment, shipIndex) => {
                const rateValue = invoice.ratePerKg || 10;
                const isAir = isAirFreight;
                
                if (shipment.items && shipment.items.length > 0) {
                  return shipment.items.map((item, itemIndex) => {
                    const itemWeight = item.weight || 0;
                    const itemVolume = item.volume || 0;
                    const amount = isAir ? (itemWeight * rateValue) : (itemVolume * rateValue);
                    const rowNum = shipIndex + itemIndex + 1;
                    
                    return '<tr>' +
                      '<td>' + rowNum + '</td>' +
                      '<td>' + (itemIndex === 0 ? shipment.shipmentNumber : '') + '</td>' +
                      '<td>' + item.description + '</td>' +
                      '<td class="right">' + item.quantity + ' ' + item.quantityUnit + '</td>' +
                      '<td class="right">' + item.boxes + '</td>' +
                      '<td class="right">' + itemWeight.toFixed(1) + '</td>' +
                      '<td class="right">' + itemVolume.toFixed(2) + '</td>' +
                      '<td class="right">' + rateValue.toFixed(2) + '</td>' +
                      '<td class="right">' + amount.toFixed(2) + '</td>' +
                    '</tr>';
                  }).join('');
                } else {
                  const weight = shipment.weight || 0;
                  const volume = shipment.volume || 0;
                  const amount = isAir ? (weight * rateValue) : (volume * rateValue);
                  
                  return '<tr>' +
                    '<td>' + (shipIndex + 1) + '</td>' +
                    '<td>' + shipment.shipmentNumber + '</td>' +
                    '<td>' + (shipment.description || 'Shipment') + '</td>' +
                    '<td class="right">—</td>' +
                    '<td class="right">' + shipment.packages + '</td>' +
                    '<td class="right">' + weight.toFixed(1) + '</td>' +
                    '<td class="right">' + volume.toFixed(2) + '</td>' +
                    '<td class="right">' + rateValue.toFixed(2) + '</td>' +
                    '<td class="right">' + amount.toFixed(2) + '</td>' +
                  '</tr>';
                }
              }).join('')}
            </tbody>
          </table>

          <!-- SHIPMENT TOTALS BOX -->
          <div class="totals-box">
            <table>
              <tr>
                <th>Total Shipments:</th>
                <td>${fullShipments.length}</td>
              </tr>
              <tr>
                <th>Total Packages:</th>
                <td>${invoice.totalPackages || 0}</td>
              </tr>
              <tr>
                <th>Total Weight:</th>
                <td>${(invoice.totalWeight || 0).toFixed(2)} kg</td>
              </tr>
              <tr>
                <th>Total Volume:</th>
                <td>${(invoice.totalVolume || 0).toFixed(2)} m³</td>
              </tr>
            </table>
          </div>

          ${invoice.lineItems && invoice.lineItems.length > 0 ? `
            <div class="section-title" style="margin-top:14px;">Additional Items</div>
            <table>
              <thead>
                <tr>
                  <th>Description</th>
                  <th class="right">Amount ($)</th>
                </tr>
              </thead>
              <tbody>
                ${invoice.lineItems.map(item => `
                  <tr>
                    <td>${item.description}</td>
                    <td class="right">${(item.amount || 0).toFixed(2)}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          ` : ''}

          <!-- FINANCIAL TOTALS -->
          <div class="totals-wrap">
            <table class="totals-table">
              <tr>
                <td class="label-col">Subtotal</td>
                <td class="val-col">$ ${(invoice.subtotal || 0).toFixed(2)}</td>
              </tr>
              ${invoice.tax > 0 ? `
                <tr>
                  <td class="label-col">Discount</td>
                  <td class="val-col" style="color:#721c24;">- $ ${(invoice.tax || 0).toFixed(2)}</td>
                </tr>
              ` : ''}
              <tr class="grand-total">
                <td class="label-col">TOTAL</td>
                <td class="val-col">$ ${(invoice.total || 0).toFixed(2)}</td>
              </tr>
              ${invoice.amountPaid > 0 ? `
                <tr>
                  <td class="label-col">Amount Paid</td>
                  <td class="val-col" style="color:#155724;">$ ${(invoice.amountPaid || 0).toFixed(2)}</td>
                </tr>
                <tr class="orange-row">
                  <td class="label-col">Balance Due</td>
                  <td class="val-col">$ ${(invoice.balance || 0).toFixed(2)}</td>
                </tr>
              ` : ''}
            </table>
          </div>

          <!-- FOOTER -->
          <div class="doc-footer">
            <strong>SourceMyGoods</strong> &nbsp;|&nbsp; Professional Sourcing, Inspection & Global Shipping<br/>
            Operated by Trust Exchange International Trading LTD<br/>
            Generated on ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}
          </div>

          <script>window.onload = function() { window.print(); }</script>
        </body>
      </html>
        `);

        printWindow.document.close();
      });
    });
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {filter === 'draft' ? `${t('draft')} ${t('invoices')}` :
               filter === 'sent' ? `${t('sent')} ${t('invoices')}` :
               filter === 'paid' ? `${t('paid')} ${t('invoices')}` :
               filter === 'overdue' ? `${t('overdue')} ${t('invoices')}` :
               filter === 'partial' ? `${t('partial')} ${t('invoices')}` :
               t('invoices')}
            </h1>
            <p className="text-gray-600">
              {filter === 'draft' ? t('viewDraftInvoices') :
               filter === 'sent' ? t('viewSentInvoices') :
               filter === 'paid' ? t('viewPaidInvoices') :
               filter === 'overdue' ? t('viewOverdueInvoices') :
               filter === 'partial' ? t('viewPartialInvoices') :
               t('generateCombinedInvoices')}
            </p>
          </div>

          {user?.role !== 'customer' && (
            <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Invoice
                </Button>
              </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>Create New Invoice</DialogTitle>
                <DialogDescription>
                  Combine multiple shipments into a single invoice
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4 max-h-[60vh] overflow-y-auto">
                {/* Step 1: Select Trip */}
                <div className="space-y-2">
                  <Label htmlFor="tripName" className="font-medium">Step 1: Select Trip *</Label>
                  <Select value={newInvoice.tripName || ""} onValueChange={(value) => {
                    // Find the selected trip
                    const selectedTrip = trips.find(t => (t.name || t.trip_number) === value);
                    
                    // Reset customer selection when trip changes
                    setNewInvoice({
                      customerCode: '',
                      customerName: '',
                      customerPhone: '',
                      tripName: value,
                      destinationWarehouse: selectedTrip?.destinationWarehouse || selectedTrip?.destination_warehouse || '',
                      rate: '10',
                      tax: '0'
                    });
                    setSelectedShipments([]);
                  }}>
                    <SelectTrigger className="border-blue-200">
                      <SelectValue placeholder="Choose a trip first" />
                    </SelectTrigger>
                    <SelectContent>
                      {trips.map(trip => (
                        <SelectItem key={trip.name || trip.trip_number} value={trip.name || trip.trip_number}>
                          {trip.name || trip.trip_number} - {trip.originWarehouse || trip.origin_warehouse} → {trip.destinationWarehouse || trip.destination_warehouse}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-gray-500">Select the trip to invoice</p>
                </div>

                {/* Step 2: Select Customer (only if trip selected) */}
                {newInvoice.tripName && (() => {
                  // Get unique customers who have shipments in this trip
                  const tripShipments = shipments.filter(s => 
                    (s.tripName || s.trip_name) === newInvoice.tripName
                  );
                  const tripCustomerCodes = [...new Set(tripShipments.map(s => s.customerCode || s.customer_code))];
                  const tripCustomers = customers.filter(c => tripCustomerCodes.includes(c.code));

                  return (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="customerCode" className="font-medium">Step 2: Select Customer *</Label>
                        <Select 
                          value={newInvoice.customerCode} 
                          onValueChange={(code) => {
                            const matchedCustomer = tripCustomers.find(c => c.code === code);
                            if (matchedCustomer) {
                              setNewInvoice({
                                ...newInvoice,
                                customerCode: code,
                                customerName: matchedCustomer.name,
                                customerPhone: matchedCustomer.phone || '',
                                destinationWarehouse: matchedCustomer.destination_warehouse || newInvoice.destinationWarehouse
                              });
                            }
                          }}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select customer with shipments in this trip" />
                          </SelectTrigger>
                          <SelectContent>
                            {tripCustomers.length === 0 && (
                              <div className="px-3 py-2 text-sm text-gray-500">
                                No customers found with shipments in this trip
                              </div>
                            )}
                            {tripCustomers.map(customer => (
                              <SelectItem key={customer.code} value={customer.code}>
                                {customer.code} - {customer.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-gray-500">
                          {tripCustomers.length} customer(s) with shipments in this trip
                        </p>
                      </div>

                      {/* Show remaining form only after customer is selected */}
                      {newInvoice.customerCode && (
                        <>
                          <div className="space-y-2">
                            <Label htmlFor="destinationWarehouse">Destination Warehouse *</Label>
                            <Input
                              id="destinationWarehouse"
                              value={newInvoice.destinationWarehouse}
                              readOnly
                              className="bg-gray-100 cursor-not-allowed"
                            />
                            <p className="text-xs text-gray-500">Auto-filled from customer's assigned warehouse</p>
                          </div>

                          <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="rate">
                                {(() => {
                                  const selectedTrip = trips.find(t => (t.name || t.trip_number) === newInvoice.tripName);
                                  const tripFreightMethod = selectedTrip?.freightMethod || selectedTrip?.freight_method || 'Air';
                                  return tripFreightMethod === 'Sea' ? 'Rate per CBM ($)' : 'Rate per KG ($)';
                                })()}
                              </Label>
                              <Input
                                id="rate"
                                type="number"
                                step="0.01"
                                value={newInvoice.rate}
                                onChange={(e) => setNewInvoice({...newInvoice, rate: e.target.value})}
                                placeholder="10.00"
                              />
                              {newInvoice.tripName && (() => {
                                const selectedTrip = trips.find(t => (t.name || t.trip_number) === newInvoice.tripName);
                                const tripFreightMethod = selectedTrip?.freightMethod || selectedTrip?.freight_method || 'Air';
                                return (
                                  <p className="text-xs text-gray-500">
                                    {tripFreightMethod === 'Sea'
                                      ? '🚢 Sea Freight - Charged by volume (CBM)'
                                      : '✈️ Air Freight - Charged by weight (KG)'}
                                  </p>
                                );
                              })()}
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="discount">Discount ($)</Label>
                              <Input
                                id="discount"
                                type="number"
                                step="0.01"
                                value={newInvoice.tax}
                                onChange={(e) => setNewInvoice({...newInvoice, tax: e.target.value})}
                                placeholder="0.00"
                              />
                            </div>
                          </div>

                          <div className="space-y-2">
                            <Label>
                              {newInvoice.customerCode
                                ? `Select Shipments for Customer ${newInvoice.customerCode}`
                                : 'Select Shipments'
                              }
                            </Label>
                            <div className="border rounded-lg p-4 space-y-2 max-h-[200px] overflow-y-auto">
                              {availableShipments.map((shipment: any) => {
                                const isAlreadyInvoiced = shipment.isInvoiced || false;

                                return (
                                  <div
                                    key={shipment.number}
                                    className={`flex items-center space-x-2 p-2 rounded ${
                                      isAlreadyInvoiced
                                        ? 'bg-gray-100 opacity-60 cursor-not-allowed'
                                        : 'hover:bg-gray-50'
                                    }`}
                                  >
                                    <Checkbox
                                      id={shipment.number}
                                      checked={selectedShipments.includes(shipment.number)}
                                      onCheckedChange={() => !isAlreadyInvoiced && handleToggleShipment(shipment.number)}
                                      disabled={isAlreadyInvoiced}
                                    />
                                    <label
                                      htmlFor={shipment.number}
                                      className={`flex-1 flex justify-between items-center text-sm ${
                                        isAlreadyInvoiced ? 'cursor-not-allowed' : 'cursor-pointer'
                                      }`}
                                    >
                                      <div className="flex items-center space-x-2">
                                        <span className={`font-medium ${isAlreadyInvoiced ? 'line-through text-gray-500' : ''}`}>
                                          {shipment.number}
                                        </span>
                                        {isAlreadyInvoiced && (
                                          <Badge variant="secondary" className="text-xs">
                                            Already Invoiced
                                          </Badge>
                                        )}
                                      </div>
                                      <span className="text-gray-600">{shipment.description || 'N/A'}</span>
                                      <span className="text-gray-500">{shipment.weight} kg, {shipment.packages} pkgs</span>
                                    </label>
                                  </div>
                                );
                              })}
                            </div>
                            {availableShipments.length === 0 && newInvoice.customerCode && (
                              <p className="text-sm text-gray-500 text-center py-4">
                                No shipments found for customer {newInvoice.customerCode} in trip {newInvoice.tripName}
                              </p>
                            )}
                            <p className="text-xs text-gray-500">{selectedShipments.length} shipment(s) selected</p>
                          </div>

                          {selectedShipments.length > 0 && (() => {
                            const selectedShipmentData = shipments.filter(s => selectedShipments.includes(s.shipmentNumber));
                            const freightMethod = selectedShipmentData[0]?.freightMethod || 'Air';
                            const isAir = freightMethod === 'Air';
                            const rate = parseFloat(newInvoice.rate || '0');
                            
                            return (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                <h4 className="font-semibold text-sm mb-2">Invoice Preview</h4>
                                <div className="space-y-1 text-sm">
                                  {selectedShipmentData.map(s => {
                                    // Show items if they exist
                                    if (s.items && s.items.length > 0) {
                                      return s.items.map((item, idx) => {
                                        const itemWeight = item.weight || 0;
                                        const itemVolume = item.volume || 0;
                                        const amount = isAir ? (itemWeight * rate) : (itemVolume * rate);
                                        return (
                                          <div key={`${s.shipmentNumber}-${idx}`} className="flex justify-between">
                                            <span>{idx === 0 ? s.shipmentNumber : ''} - {item.description}</span>
                                            <span>${amount.toFixed(2)}</span>
                                          </div>
                                        );
                                      });
                                    } else {
                                      const amount = isAir ? (s.weight * rate) : (s.volume * rate);
                                      return (
                                        <div key={s.shipmentNumber} className="flex justify-between">
                                          <span>{s.shipmentNumber} - {s.description || 'No description'}</span>
                                          <span>${amount.toFixed(2)}</span>
                                        </div>
                                      );
                                    }
                                  })}
                                  <div className="border-t pt-1 mt-2 font-medium">
                                    <div className="flex justify-between">
                                      <span>Subtotal:</span>
                                      <span>
                                        ${selectedShipmentData
                                          .reduce((sum, s) => {
                                            if (s.items && s.items.length > 0) {
                                              return sum + s.items.reduce((itemSum, item) => {
                                                const itemWeight = item.weight || 0;
                                                const itemVolume = item.volume || 0;
                                                const amount = isAir ? (itemWeight * rate) : (itemVolume * rate);
                                                return itemSum + amount;
                                              }, 0);
                                            } else {
                                              const amount = isAir ? (s.weight * rate) : (s.volume * rate);
                                              return sum + amount;
                                            }
                                          }, 0)
                                          .toFixed(2)}
                                      </span>
                                    </div>
                                    {parseFloat(newInvoice.tax || '0') > 0 && (
                                      <div className="flex justify-between text-green-600">
                                        <span>Discount:</span>
                                        <span>
                                          -${parseFloat(newInvoice.tax || '0').toFixed(2)}
                                        </span>
                                      </div>
                                    )}
                                    <div className="flex justify-between text-lg border-t pt-1 mt-1">
                                      <span>Total:</span>
                                      <span>
                                        ${(selectedShipmentData
                                          .reduce((sum, s) => {
                                            if (s.items && s.items.length > 0) {
                                              return sum + s.items.reduce((itemSum, item) => {
                                                const itemWeight = item.weight || 0;
                                                const itemVolume = item.volume || 0;
                                                const amount = isAir ? (itemWeight * rate) : (itemVolume * rate);
                                                return itemSum + amount;
                                              }, 0);
                                            } else {
                                              const amount = isAir ? (s.weight * rate) : (s.volume * rate);
                                              return sum + amount;
                                            }
                                          }, 0) - parseFloat(newInvoice.tax || '0'))
                                          .toFixed(2)}
                                      </span>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                        </>
                      )}
                    </>
                  );
                })()}
              </div>
              <DialogFooter>
                <Button
                  onClick={handleCreateInvoice}
                  disabled={!newInvoice.tripName || !newInvoice.customerCode || selectedShipments.length === 0}
                >
                  Create Invoice
                </Button>
                {!newInvoice.tripName && (
                  <p className="text-xs text-gray-500">Step 1: Select a trip</p>
                )}
                {newInvoice.tripName && !newInvoice.customerCode && (
                  <p className="text-xs text-gray-500">Step 2: Select a customer</p>
                )}
                {newInvoice.tripName && newInvoice.customerCode && selectedShipments.length === 0 && (
                  <p className="text-xs text-gray-500">Step 3: Select at least one shipment</p>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
          )}

          {/* Edit Invoice Dialog */}
          <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
            <DialogContent className="sm:max-w-[700px] max-h-[85vh]">
              <DialogHeader>
                <DialogTitle className="text-xl">Edit Invoice</DialogTitle>
                <DialogDescription className="text-sm">
                  Update invoice details and status
                </DialogDescription>
              </DialogHeader>
              {editingInvoice && (
                <div className="grid gap-4 py-4 max-h-[65vh] overflow-y-auto pr-2">
                  <div className="space-y-2">
                    <Label htmlFor="editInvoiceNumber" className="text-sm font-medium">Invoice Number</Label>
                    <Input
                      id="editInvoiceNumber"
                      value={editingInvoice.invoiceNumber}
                      className="bg-gray-50 truncate"
                      readOnly
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="editCustomerCode" className="text-sm font-medium">Customer Code</Label>
                      <Input
                        id="editCustomerCode"
                        value={editingInvoice.customerCode}
                        onChange={(e) => setEditingInvoice({...editingInvoice, customerCode: e.target.value})}
                        maxLength={4}
                        className="font-mono"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="editStatus" className="text-sm font-medium">Status</Label>
                      <Select
                        value={editingInvoice.status}
                        onValueChange={(value: Invoice['status']) => setEditingInvoice({...editingInvoice, status: value})}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Draft">Draft</SelectItem>
                          <SelectItem value="Sent">Sent</SelectItem>
                          <SelectItem value="Paid">Paid</SelectItem>
                          <SelectItem value="Overdue">Overdue</SelectItem>
                          <SelectItem value="Partial">Partial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="editCustomerName" className="text-sm font-medium">Customer Name</Label>
                    <Input
                      id="editCustomerName"
                      value={editingInvoice.customerName}
                      onChange={(e) => setEditingInvoice({...editingInvoice, customerName: e.target.value})}
                      className="w-full"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="editCustomerPhone" className="text-sm font-medium">Customer Phone</Label>
                    <Input
                      id="editCustomerPhone"
                      type="tel"
                      value={editingInvoice.customerPhone || ''}
                      onChange={(e) => setEditingInvoice({...editingInvoice, customerPhone: e.target.value || undefined})}
                      placeholder="+218 912345678"
                      className="w-full"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="editTripName" className="text-sm font-medium">Trip Name</Label>
                    <Input
                      id="editTripName"
                      value={editingInvoice.tripName || ''}
                      onChange={(e) => setEditingInvoice({...editingInvoice, tripName: e.target.value || undefined})}
                      placeholder="Optional trip name"
                      className="w-full"
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="editIssueDate" className="text-sm font-medium">Issue Date</Label>
                      <Input
                        id="editIssueDate"
                        type="date"
                        value={editingInvoice.issueDate}
                        onChange={(e) => setEditingInvoice({...editingInvoice, issueDate: e.target.value})}
                        className="w-full"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="editDueDate" className="text-sm font-medium">Due Date</Label>
                      <Input
                        id="editDueDate"
                        type="date"
                        value={editingInvoice.dueDate}
                        onChange={(e) => setEditingInvoice({...editingInvoice, dueDate: e.target.value})}
                        className="w-full"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="editRate" className="text-sm font-medium">Rate per kg ($)</Label>
                      <Input
                        id="editRate"
                        type="number"
                        step="0.01"
                        value={editingInvoice.shipments[0]?.rate || 0}
                        onChange={(e) => {
                          const newRate = parseFloat(e.target.value) || 0;
                          const updatedShipments = editingInvoice.shipments.map(shipment => ({
                            ...shipment,
                            rate: newRate,
                            amount: shipment.weight * newRate
                          }));
                          const subtotal = updatedShipments.reduce((sum, s) => sum + s.amount, 0);

                          setEditingInvoice({
                            ...editingInvoice,
                            shipments: updatedShipments,
                            subtotal,
                            total: subtotal + (editingInvoice.tax || 0)
                          });
                        }}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="editDiscount" className="text-sm font-medium">Discount ($)</Label>
                      <Input
                        id="editDiscount"
                        type="number"
                        step="0.01"
                        value={editingInvoice.tax || 0}
                        onChange={(e) => {
                          const newDiscount = parseFloat(e.target.value) || 0;

                          setEditingInvoice({
                            ...editingInvoice,
                            tax: newDiscount,
                            total: editingInvoice.subtotal - newDiscount
                          });
                        }}
                      />
                    </div>
                  </div>

                  {/* Amount Paid Field */}
                  <div className="space-y-2">
                    <Label htmlFor="amountPaid" className="text-sm font-medium">Amount Paid ($)</Label>
                    <Input
                      id="amountPaid"
                      type="number"
                      step="0.01"
                      value={editingInvoice.amountPaid || 0}
                      onChange={(e) => {
                        const paid = parseFloat(e.target.value) || 0;
                        const balance = editingInvoice.total - paid;

                        setEditingInvoice({
                          ...editingInvoice,
                          amountPaid: paid,
                          balance: balance,
                          balanceDue: balance,
                          status: balance === 0 ? 'Paid' : balance < editingInvoice.total ? 'Partial' : editingInvoice.status
                        });
                      }}
                    />
                    <p className="text-xs text-gray-500">
                      Enter partial or full payment amount
                    </p>
                  </div>

                  <div className="space-y-2">
                    <Label className="text-sm font-medium">Shipments ({editingInvoice.shipments.length})</Label>
                    <div className="border rounded-lg p-3 space-y-2 max-h-[240px] overflow-y-auto bg-gray-50">
                      {editingInvoice.shipments.map((shipment, index) => {
                        const isSeaFreight = shipment.freightMethod === 'Sea';
                        return (
                          <div key={index} className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2 p-3 bg-white rounded shadow-sm border">
                            <div className="flex items-center space-x-2 min-w-0 flex-1">
                              <Package className="h-4 w-4 text-blue-600 flex-shrink-0" />
                              <span className="font-semibold text-gray-900 truncate">{shipment.shipmentNumber}</span>
                            </div>
                            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600">
                              <span className="whitespace-nowrap">
                                {isSeaFreight ? `${(shipment.volume || 0).toFixed(2)} CBM` : `${shipment.weight} kg`}
                              </span>
                              <span className="whitespace-nowrap">{shipment.packages} pkgs</span>
                              <span className="whitespace-nowrap">@ ${(shipment.rate || 0).toFixed(2)}/{isSeaFreight ? 'CBM' : 'kg'}</span>
                              <span className="font-semibold text-gray-900 whitespace-nowrap">${(shipment.amount || 0).toFixed(2)}</span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <div className="space-y-3 border-t pt-4 bg-blue-50 p-4 rounded-lg">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-700">Subtotal:</span>
                      <span className="font-semibold text-gray-900 text-lg">${(editingInvoice.subtotal || 0).toFixed(2)}</span>
                    </div>
                    {editingInvoice.tax > 0 && (
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-700">Discount:</span>
                        <span className="font-semibold text-green-600">-${(editingInvoice.tax || 0).toFixed(2)}</span>
                      </div>
                    )}
                    <div className="flex justify-between items-center border-t border-blue-200 pt-3">
                      <span className="text-base font-bold text-gray-900">Total Amount:</span>
                      <span className="text-2xl font-bold text-blue-600">${(editingInvoice.total || 0).toFixed(2)}</span>
                    </div>
                    {editingInvoice.amountPaid > 0 && (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-gray-700">Amount Paid:</span>
                          <span className="font-semibold text-green-600">${(editingInvoice.amountPaid || 0).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between items-center border-t border-blue-200 pt-2">
                          <span className="text-sm font-bold text-gray-900">Balance Due:</span>
                          <span className="text-lg font-bold text-orange-600">${(editingInvoice.balance || 0).toFixed(2)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handleUpdateInvoice}>
                  Update Invoice
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search invoices by number or customer..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>

          {/* View Toggle */}
          <div className="flex border rounded-md">
            <Button
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('grid')}
              className="rounded-r-none"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className="rounded-l-none"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>

          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowFilters(!showFilters)}
            className="w-full sm:w-auto"
          >
            <Filter className="h-4 w-4 mr-2" />
            Filters
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
            className="w-full sm:w-auto"
          >
            <Calendar className="h-4 w-4 mr-2" />
            History
          </Button>
        </div>

        {/* Filter Dropdowns */}
        {showFilters && (
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Warehouse Filter */}
                <div className="space-y-2">
                  <Label htmlFor="warehouse-filter">Filter by Warehouse</Label>
                  <Select value={warehouseFilter} onValueChange={setWarehouseFilter}>
                    <SelectTrigger id="warehouse-filter">
                      <SelectValue placeholder="All Warehouses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Warehouses</SelectItem>
                      {uniqueWarehouses.map(warehouse => (
                        <SelectItem key={warehouse} value={warehouse}>
                          {warehouse}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Trip Filter */}
                <div className="space-y-2">
                  <Label htmlFor="trip-filter">Filter by Trip</Label>
                  <Select value={tripFilter} onValueChange={setTripFilter}>
                    <SelectTrigger id="trip-filter">
                      <SelectValue placeholder="All Trips" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Trips</SelectItem>
                      {uniqueTrips.map(trip => (
                        <SelectItem key={trip} value={trip}>
                          {trip}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Date Range History */}
        {showHistory && (
          <Card>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Date Range History</h3>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowHistory(false)}
                >
                  ✕
                </Button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="startDate">Start Date</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={dateRange.startDate}
                    onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
                    placeholder="Select start date"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="endDate">End Date</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={dateRange.endDate}
                    onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
                    placeholder="Select end date"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    if (dateRange.startDate && dateRange.endDate) {
                      // Filter invoices by date range
                      const filtered = filteredInvoices.filter(invoice => {
                        const invoiceDate = new Date(invoice.issueDate);
                        const start = new Date(dateRange.startDate);
                        const end = new Date(dateRange.endDate);
                        end.setHours(23, 59, 59, 999); // Include end date fully
                        return invoiceDate >= start && invoiceDate <= end;
                      });
                      alert(`Found ${filtered.length} invoices between ${dateRange.startDate} and ${dateRange.endDate}`);
                    } else {
                      alert('Please select both start and end dates');
                    }
                  }}
                  className="flex-1"
                >
                  Apply Date Filter
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setDateRange({ startDate: '', endDate: '' });
                    setShowHistory(false);
                  }}
                >
                  Clear
                </Button>
              </div>
              {/* Summary Statistics */}
              {dateRange.startDate && dateRange.endDate && (() => {
                // Filter invoices by date range
                const dateFilteredInvoices = filteredInvoices.filter(invoice => {
                  const invoiceDate = new Date(invoice.issueDate);
                  const start = new Date(dateRange.startDate);
                  const end = new Date(dateRange.endDate);
                  end.setHours(23, 59, 59, 999);
                  return invoiceDate >= start && invoiceDate <= end;
                });

                return (
                  <div className="border-t pt-4 mt-4">
                    <h4 className="font-medium mb-3">Summary for {dateRange.startDate} to {dateRange.endDate}</h4>
                    <div className="text-sm text-gray-600 mb-3">{dateFilteredInvoices.length} invoices found</div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {['Draft', 'Sent', 'Paid', 'Overdue'].map(status => {
                        const statusInvoices = dateFilteredInvoices.filter(i => i.status === status);
                        const count = statusInvoices.length;
                        const total = statusInvoices.reduce((sum, i) => sum + (parseFloat(i.total) || 0), 0);
                        
                        return (
                          <div key={status} className="text-center p-3 bg-white rounded border">
                            <div className="text-2xl font-bold text-gray-900">{count}</div>
                            <div className="text-sm text-gray-600">{status}</div>
                            <div className="text-xs text-gray-500 mt-1">${total.toLocaleString()}</div>
                          </div>
                        );
                      })}
                    </div>
                    
                    {/* Total Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                      <div className="text-center p-3 bg-green-50 rounded border border-green-200">
                        <div className="text-xl font-bold text-green-900">
                          ${dateFilteredInvoices.reduce((sum, i) => sum + (parseFloat(i.total) || 0), 0).toLocaleString()}
                        </div>
                        <div className="text-sm text-green-700">Total Amount</div>
                      </div>
                      <div className="text-center p-3 bg-blue-50 rounded border border-blue-200">
                        <div className="text-xl font-bold text-blue-900">
                          ${dateFilteredInvoices.reduce((sum, i) => sum + (parseFloat(i.amountPaid) || 0), 0).toLocaleString()}
                        </div>
                        <div className="text-sm text-blue-700">Total Paid</div>
                      </div>
                      <div className="text-center p-3 bg-orange-50 rounded border border-orange-200">
                        <div className="text-xl font-bold text-orange-900">
                          ${dateFilteredInvoices.reduce((sum, i) => sum + (parseFloat(i.balance) || 0), 0).toLocaleString()}
                        </div>
                        <div className="text-sm text-orange-700">Outstanding Balance</div>
                      </div>
                    </div>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        )}

        {/* Invoice Cards */}
        {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 gap-4">
          {filteredInvoices.map((invoice, index) => (
            <Card 
              key={invoice.id} 
              className="hover:shadow-xl transition-all duration-300 hover:border-blue-300 border-l-4 border-l-emerald-500 hover:scale-[1.01]"
              style={{ animationDelay: `${index * 30}ms` }}
            >
              <CardHeader className="pb-3">
                <div className="flex flex-col lg:flex-row lg:justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3 mb-2">
                      <FileText className="h-5 w-5 text-blue-600 flex-shrink-0" />
                      <CardTitle className="text-lg truncate">{invoice.invoiceNumber}</CardTitle>
                      <CopyButton text={invoice.invoiceNumber} label="Invoice Number" size="icon" />
                      <Badge className={`${getStatusColor(invoice.status)} flex items-center space-x-1 whitespace-nowrap`}>
                        {getStatusIcon(invoice.status)}
                        <span>{invoice.status}</span>
                      </Badge>
                    </div>
                    <div className="flex items-center space-x-2">
                      <QRCodeDisplay
                        value={generateInvoiceQRValue(invoice.invoiceNumber)}
                        size={64}
                        showLabel={false}
                      />
                      <span className="text-xs text-gray-500">Scan to update</span>
                    </div>
                  </div>
                  <div className="min-w-0">
                    <CardDescription className="flex flex-col space-y-1.5">
                      <div className="flex items-center space-x-2 min-w-0">
                        <User className="h-4 w-4 flex-shrink-0" />
                        <span className="truncate">{invoice.customerName}</span>
                        <span className="text-xs whitespace-nowrap">(Code: {invoice.customerCode})</span>
                        <CopyButton text={invoice.customerCode} label="Customer Code" size="icon" />
                      </div>
                      {invoice.customerPhone && (
                        <div className="flex items-center space-x-2 min-w-0">
                          <Phone className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">{invoice.customerPhone}</span>
                        </div>
                      )}
                      {invoice.customerEmail && (
                        <div className="flex items-center space-x-2 min-w-0">
                          <Mail className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">{invoice.customerEmail}</span>
                        </div>
                      )}
                      {invoice.tripName && (
                        <div className="flex items-center space-x-2 min-w-0">
                          <Truck className="h-4 w-4 flex-shrink-0" />
                          <span className="truncate">Trip: {invoice.tripName}</span>
                        </div>
                      )}
                    </CardDescription>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePrintInvoice(invoice)}
                    >
                      <Printer className="h-4 w-4 mr-1" />
                      Print
                    </Button>
                    {user?.role !== 'customer' && (
                      <>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditInvoice(invoice)}
                        >
                          <FileText className="h-4 w-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteInvoice(invoice.invoiceNumber)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* Invoice Summary */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Total Amount</div>
                    <div className="text-xl font-bold text-gray-900 flex items-center">
                      <DollarSign className="h-4 w-4 mr-1" />
                      {(invoice.total || 0).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Shipments</div>
                    <div className="text-xl font-bold text-gray-900 flex items-center">
                      <Package className="h-4 w-4 mr-1" />
                      {invoice.shipments?.length || 0}
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Total Weight</div>
                    <div className="text-xl font-bold text-gray-900">
                      {(invoice.totalWeight || 0).toFixed(1)} kg
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Total Volume</div>
                    <div className="text-xl font-bold text-gray-900">
                      {(invoice.totalVolume || 0).toFixed(2)} m³
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-500 mb-1">Total Packages</div>
                    <div className="text-xl font-bold text-gray-900">
                      {invoice.totalPackages || 0}
                    </div>
                  </div>
                </div>

                {/* Invoice Details */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4 text-sm">
                  <div className="flex items-center text-gray-600">
                    <Calendar className="h-4 w-4 mr-2" />
                    <span>Issued: {invoice.issueDate}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Calendar className="h-4 w-4 mr-2" />
                    <span>Due: {invoice.dueDate}</span>
                  </div>
                  {invoice.paidDate && (
                    <div className="flex items-center text-green-600">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      <span>Paid: {invoice.paidDate}</span>
                    </div>
                  )}
                </div>

                {/* Shipments List */}
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-3 text-sm text-gray-700">Included Shipments:</h4>
                  <div className="space-y-2">
                    {invoice.shipments.map((shipment, index) => {
                      const isAirFreight = shipment.freightMethod === 'Air';
                      const rateUnit = isAirFreight ? '/kg' : '/CBM';
                      const quantityDisplay = isAirFreight 
                        ? `${shipment.weight} kg` 
                        : `${(shipment.volume || 0).toFixed(2)} CBM`;
                      
                      return (
                        <div key={index} className="flex justify-between items-center p-3 bg-white border rounded-lg text-sm">
                          <div className="flex items-center space-x-3">
                            <Package className="h-4 w-4 text-gray-400" />
                            <span className="font-medium">{shipment.shipmentNumber}</span>
                          </div>
                          <div className="flex items-center space-x-6 text-gray-600">
                            <span>{quantityDisplay}</span>
                            <span>{shipment.packages} pkgs</span>
                            <span>@ ${(shipment.rate || 0).toFixed(2)}{rateUnit}</span>
                            <span className="font-semibold text-gray-900">${(shipment.amount || 0).toFixed(2)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Invoice Totals */}
                  <div className="mt-4 space-y-2 text-sm border-t pt-4">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Subtotal:</span>
                      <span className="font-medium">${(invoice.subtotal || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tax (10%):</span>
                      <span className="font-medium">${(invoice.tax || 0).toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between text-lg font-bold border-t pt-2">
                      <span>Total:</span>
                      <span>${(invoice.total || 0).toFixed(2)}</span>
                    </div>
                  </div>

                  {/* Status Update Buttons */}
                  {invoice.status !== 'Paid' && (
                    <div className="mt-4 flex space-x-2">
                      {user?.role === 'customer' ? (
                        // Customer: Pay with Wallet button
                        (invoice.status === 'Sent' || invoice.status === 'Overdue' || invoice.status === 'Partial') && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedInvoiceForPayment(invoice);
                              setShowPayDialog(true);
                            }}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <CreditCard className="h-4 w-4 mr-2" />
                            Pay with Wallet
                          </Button>
                        )
                      ) : (
                        // Admin/Warehouse: Status update buttons
                        <>
                          {invoice.status === 'Draft' && (
                            <Button
                              size="sm"
                              onClick={() => updateInvoiceStatus(invoice.invoiceNumber, 'Sent')}
                            >
                              Mark as Sent
                            </Button>
                          )}
                          {(invoice.status === 'Sent' || invoice.status === 'Overdue') && (
                            <Button
                              size="sm"
                              onClick={() => updateInvoiceStatus(invoice.invoiceNumber, 'Paid')}
                            >
                              Mark as Paid
                            </Button>
                          )}
                        </>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
        ) : (
          /* List View */
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Invoice</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Trip/Warehouse</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dates</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredInvoices.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-gray-50">
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">{invoice.invoiceNumber}</div>
                          <div className="text-sm text-gray-500">{invoice.shipments?.length || 0} shipments</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{invoice.customerName}</div>
                          <div className="text-sm text-gray-500 font-mono">{invoice.customerCode}</div>
                        </td>
                        <td className="px-4 py-4">
                          <div className="text-sm text-gray-900">{invoice.tripName || '—'}</div>
                          <div className="text-sm text-gray-500">{invoice.destinationWarehouse}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">Issue: {invoice.issueDate}</div>
                          <div className="text-sm text-gray-500">Due: {invoice.dueDate}</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right">
                          <div className="text-sm font-medium text-gray-900">${parseFloat(invoice.total || '0').toFixed(2)}</div>
                          <div className="text-sm text-gray-500">${parseFloat(invoice.balance || '0').toFixed(2)} due</div>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap">
                          <Badge className={
                            invoice.status === 'Paid' ? 'bg-green-100 text-green-800' :
                            invoice.status === 'Sent' ? 'bg-blue-100 text-blue-800' :
                            invoice.status === 'Overdue' ? 'bg-red-100 text-red-800' :
                            invoice.status === 'Partial' ? 'bg-yellow-100 text-yellow-800' :
                            'bg-gray-100 text-gray-800'
                          }>
                            {invoice.status}
                          </Badge>
                        </td>
                        <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end space-x-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handlePrintInvoice(invoice)}
                              title="Print Invoice"
                            >
                              <Printer className="h-4 w-4" />
                            </Button>
                            {user?.role !== 'customer' && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setEditingInvoice(invoice);
                                    setIsEditDialogOpen(true);
                                  }}
                                  title="Edit"
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteInvoice(invoice.id)}
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}

        {filteredInvoices.length === 0 && (
          <Card className="border-dashed">
            <CardContent className="py-12 text-center">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No invoices found</h3>
              <p className="text-gray-500">
                {searchTerm ? 'No invoices match your search criteria.' : ''}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Pay with Wallet Dialog */}
      {selectedInvoiceForPayment && (
        <PayWithWalletDialog
          open={showPayDialog}
          onOpenChange={setShowPayDialog}
          type="invoice"
          referenceNumber={selectedInvoiceForPayment.invoiceNumber}
          amount={selectedInvoiceForPayment.balance || selectedInvoiceForPayment.total}
          onSuccess={() => {
            // Refresh invoices after successful payment
            window.location.reload();
          }}
        />
      )}
    </Layout>
  );
}